import React, { FC, useMemo, useState } from 'react'
import classNames from 'classnames'
import { InView } from 'react-intersection-observer'
import { Button } from '@/atoms/Button'
import { FlexColumn } from '@/atoms/FlexContainers'
import { Image } from '@/atoms/Image'
import { AsH2, CaptionLG, HeadingXS, ParagraphXL, Text } from '@/atoms/Text'
import { slugs } from '@/constants/slugs'
import { useExperimentContext } from '@/experimentation/hooks'
import { AutoBookModal } from '@/organisms/AutoBookModal'
import { TheaterPreferencesModal, TheaterPreferencesProvider } from '@/organisms/TheaterPreferences'
import { TheatricalReleaseResult } from '@/services/Theatrical'
import { useUser } from '@/services/UserService'
import { useEcommerceEvents } from '@/utils/analytics/ecommerce-events'
import { useTranslate } from '@/utils/translate/translate-client'
import { useSweepstakeData } from '@/views/PreSales/PreSalesGiveawayView/hooks/useSweepstakeData'
import { EmptyStateGiveaway } from '@/views/TicketCheckoutViews/ShowtimesView/components/EmptyStateGiveaway'
import { PresaleVenueItem } from '@/views/TicketCheckoutViews/ShowtimesView/components/PresaleVenueItem'
import { VenueItem } from '@/views/TicketCheckoutViews/ShowtimesView/components/VenueItem'
import { GroupTicketsModalTypes } from '@/views/TicketCheckoutViews/ShowtimesView/components/modals/GroupTicketsModal/GroupTicketsModal'
import { useGuildTickets } from '@/views/TicketCheckoutViews/hooks/useGuildTickets'
import { ShowtimesVenue } from '../hooks/useVenueSearch/useVenueSearch'
import { useTicketsContext } from './TicketsContext'
import { GuildTicketsBanner } from './banners/GuildTicketsBanner'

export interface LocaleWarningModalState {
  isOpen: boolean
  warning: string
  callback: () => void
}

interface VenueListProps {
  venues: ShowtimesVenue[]
  theatricalRelease: TheatricalReleaseResult
  theaterSlug: string
  reservationId?: string
  className?: string
  isGroupTicketsModalOpen: boolean
  openGroupTicketsModal: (type: GroupTicketsModalTypes) => void
  setLocaleWarningModalState: React.Dispatch<React.SetStateAction<LocaleWarningModalState>>
  theatricalSlug: string
  showGuildTicketBanner: boolean
  showGiveawayEmptyState: boolean
  shouldHideEmptyState?: boolean
}

const VenueList: FC<VenueListProps> = ({
  venues,
  theatricalRelease,
  theaterSlug,
  reservationId,
  isGroupTicketsModalOpen,
  openGroupTicketsModal,
  setLocaleWarningModalState,
  className,
  theatricalSlug,
  showGuildTicketBanner,
  showGiveawayEmptyState,
  shouldHideEmptyState = false,
}) => {
  const [isAutoBookModalOpen, setIsAutoBookModalOpen] = useState(false)
  const [isPreferencesModalOpen, setIsPreferencesModalOpen] = useState(false)
  const isEmpty = venues?.length === 0
  const { trackProductViewed } = useEcommerceEvents()
  const { isLoggedIn } = useUser()
  const { areGuildTicketsAvailable } = useGuildTickets(theatricalSlug)
  const { disableShowtimes, setDisableShowtimes, basicPageExperimentEnabled } = useTicketsContext()
  const { getFeatureValue } = useExperimentContext()

  const shouldShowNewBadge = useMemo(() => {
    const hasNewShowtimes = venues?.some((venue) => venue?.showtimes?.some((showtime) => showtime?.isNew))
    return hasNewShowtimes && getFeatureValue('new-showtime-badge', false)
  }, [venues, getFeatureValue])

  if (isEmpty && shouldHideEmptyState) return null

  if (isEmpty)
    return <VenueListEmptyState theatricalSlug={theatricalSlug} showGiveawayEmptyState={showGiveawayEmptyState} />

  return (
    <>
      <TheaterPreferencesProvider>
        <InView
          as="ul"
          className={classNames(
            'flex flex-col gap-2',
            basicPageExperimentEnabled &&
              'md:grid md:grid-cols-[repeat(auto-fill,minmax(340px,1fr))] lg:grid-cols-[repeat(auto-fill,minmax(416px,1fr))] md:gap-3',
            className,
          )}
          triggerOnce
          onChange={(inView) => {
            if (!inView) return

            trackProductViewed({
              category: 'theatrical ticket',
              funnel: 'theatrical',
              sku: `ticket-${theaterSlug}`,
              product_id: `ticket-${theaterSlug}`,
              name: `${theaterSlug} theatrical ticket`,
              price: 15,
              currency: 'USD',
              quantity: 1,
              projectSlug: theaterSlug,
            })
          }}
        >
          {venues.map((venue, idx) => {
            const showGuildBanner = idx === 0 && venues.length > 1 && areGuildTicketsAvailable && isLoggedIn

            return (
              <React.Fragment key={venue.id}>
                {!venue.showtimes.length &&
                venue.isPresaleCompatible &&
                theatricalRelease?.region?.ticketsStatus === 'PRE_SALES' ? (
                  <PresaleVenueItem venue={venue} theatricalRelease={theatricalRelease} />
                ) : (
                  <VenueItem
                    key={venue.id}
                    theaterSlug={theaterSlug}
                    reservationId={reservationId}
                    theatricalRelease={theatricalRelease}
                    venue={venue}
                    setLocaleWarningModalState={setLocaleWarningModalState}
                    isGroupTicketsModalOpen={isGroupTicketsModalOpen}
                    openGroupTicketsModal={openGroupTicketsModal}
                    handleOpenAutoBookModal={() => setIsAutoBookModalOpen(true)}
                    handleOpenPreferencesModal={() => setIsPreferencesModalOpen(true)}
                    zIndex={venues.length - idx}
                    showtimesDisabled={disableShowtimes}
                    setDisableShowtimes={setDisableShowtimes}
                    shouldShowNewBadge={shouldShowNewBadge}
                  />
                )}
                {showGuildBanner && showGuildTicketBanner && (
                  <FlexColumn className="w-full items-center gap-6 text-center md:hidden">
                    <GuildTicketsBanner
                      shouldUseGradient
                      theatricalSlug={theatricalSlug}
                      projectName={theatricalRelease?.title ?? ''}
                      className="relative z-10 w-full overflow-hidden rounded-2xl md:hidden"
                      mobileClassName="text-left z-[11]"
                    />
                  </FlexColumn>
                )}
              </React.Fragment>
            )
          })}
          <AutoBookModal open={isAutoBookModalOpen} onClose={() => setIsAutoBookModalOpen(false)} />
          <TheaterPreferencesModal isOpen={isPreferencesModalOpen} onClose={() => setIsPreferencesModalOpen(false)} />
        </InView>
      </TheaterPreferencesProvider>
    </>
  )
}
export default VenueList

const VenueListEmptyState = ({
  theatricalSlug,
  showGiveawayEmptyState,
}: {
  theatricalSlug: string
  showGiveawayEmptyState: boolean
}) => {
  const { t } = useTranslate('tickets')
  const { isActive, sweepstakeLoading } = useSweepstakeData(theatricalSlug)
  const { releaseTypeFilter, setReleaseTypeFilter } = useTicketsContext()

  if (sweepstakeLoading) return null

  if (theatricalSlug === slugs.david && releaseTypeFilter.includes('SING_ALONG')) {
    return (
      <FlexColumn className="mb-6 items-center text-center">
        <Image
          priority
          className="w-[224px]"
          alt=""
          src="/v1767042961/david/young-david-singalong"
          width={224}
          height={91}
        />
        <FlexColumn className="gap-4">
          <HeadingXS weight="bold">{t('singAlongShowtimesComingSoon', 'Sing-Along Showtimes Coming Soon')}</HeadingXS>
          <ParagraphXL className="max-w-lg text-balance">
            {t('noSingAlongShowtimesAvailable', 'No Sing-Along showtimes for this date or location yet.')}
          </ParagraphXL>
          <CaptionLG color="core-gray-700">{t('checkBackLater', 'Try another date, or view all showtimes.')}</CaptionLG>
          <div className="mt-2 flex justify-center">
            <Button
              variant="black"
              onClick={() => {
                setReleaseTypeFilter([])
              }}
            >
              {t('viewAllShowtimes', 'View All Showtimes')}
            </Button>
          </div>
        </FlexColumn>
      </FlexColumn>
    )
  }

  if (isActive && showGiveawayEmptyState) {
    return <EmptyStateGiveaway theatricalSlug={theatricalSlug} />
  }

  return (
    <FlexColumn className="my-6 items-center gap-6 text-center">
      <Image
        priority
        className="md:!h-[136px] md:!w-[180px]"
        alt=""
        src="/v1712858189/share-seats-illustration-colored-02_2x.webp"
        width={120}
        height={91}
      />
      <FlexColumn className="gap-2">
        <Text as={AsH2} className="photon-title-sm md:photon-title-md font-semibold" weight="bold">
          {t('noShowtimesAvailable', 'No Showtimes Available')}
        </Text>
        <Text className="photon-paragraph-sm md:photon-paragraph-md max-w-lg" color="core-gray-700">
          {t(
            'atTheMomentTheresNoShowtimesAvailable',
            "At the moment, there aren't any showtimes in your area. Feel free to explore other locations or try another date!",
          )}
        </Text>
      </FlexColumn>
    </FlexColumn>
  )
}
